import { spawn } from "node:child_process";
import { access } from "node:fs/promises";
import path from "node:path";

export interface DatabricksCommandResult {
  exitCode: number;
  stdout: string;
  stderr: string;
}

export type DatabricksCommandRunner = (
  command: string,
  args: readonly string[],
  options: { cwd: string; env: NodeJS.ProcessEnv },
) => Promise<DatabricksCommandResult>;

export interface DeployDatabricksAppArtifactOptions {
  /** Immutable Fabric Harness `databricks-app` build directory. */
  artifactDir: string;
  /** Databricks CLI profile. App identity/environment auth is used when omitted. */
  profile?: string;
  /** Asset Bundle target selected by the governed release environment. */
  target?: string;
  /** Databricks CLI executable; defaults to `databricks`. */
  executable?: string;
  env?: NodeJS.ProcessEnv;
  /** Injectable command boundary for tests and managed runners. */
  run?: DatabricksCommandRunner;
  onCommand?: (command: { executable: string; args: readonly string[] }) => void;
  /** Explicit Databricks App resource to activate after bundle synchronization. */
  appName?: string;
  /** Bundle-synchronized workspace source path used for snapshot activation. */
  sourceCodePath?: string;
  /** App deployment mode; Databricks Apps currently supports SNAPSHOT here. */
  mode?: "SNAPSHOT";
  /** Start stopped App compute before activating the snapshot. */
  startBeforeDeploy?: boolean;
}

export interface DeployDatabricksAppArtifactResult {
  bundleSynchronized: true;
  appSnapshotActivated: true;
  bundleOutput: string;
  appOutput: string;
}

export interface ValidateDatabricksAppArtifactOptions {
  artifactDir: string;
  profile?: string;
  target?: string;
  executable?: string;
  env?: NodeJS.ProcessEnv;
  run?: DatabricksCommandRunner;
  onCommand?: (command: { executable: string; args: readonly string[] }) => void;
}

export interface ValidateDatabricksAppArtifactResult {
  valid: true;
  output: string;
}

export interface DestroyDatabricksAppArtifactOptions extends ValidateDatabricksAppArtifactOptions {}

export interface DestroyDatabricksAppArtifactResult {
  destroyed: true;
  output: string;
}

/** Validate an already-built Databricks App artifact without mutating the workspace. */
export async function validateDatabricksAppArtifact(
  options: ValidateDatabricksAppArtifactOptions,
): Promise<ValidateDatabricksAppArtifactResult> {
  const artifactDir = path.resolve(options.artifactDir);
  await access(path.join(artifactDir, "databricks.yml"));
  const run = options.run ?? runDatabricksCommand;
  const args = [
    "bundle",
    "validate",
    ...(options.target ? ["--target", options.target] : []),
    ...(options.profile ? ["--profile", options.profile] : []),
  ] as const;
  const executable = options.executable ?? "databricks";
  options.onCommand?.({ executable, args });
  const result = await run(executable, args, {
    cwd: artifactDir,
    env: options.env ?? process.env,
  });
  requireSuccess("Databricks bundle validation", result);
  return { valid: true, output: lastNonEmptyLine(result.stdout) };
}

/**
 * Deploy an already-built Fabric Harness Databricks App artifact.
 *
 * This is the programmatic counterpart of `fh deploy --target databricks-app`.
 * It intentionally does not rebuild the artifact: release orchestrators can verify
 * the immutable digest first, then use this function for the Databricks side effect.
 */
export async function deployDatabricksAppArtifact(
  options: DeployDatabricksAppArtifactOptions,
): Promise<DeployDatabricksAppArtifactResult> {
  const artifactDir = path.resolve(options.artifactDir);
  await access(path.join(artifactDir, "databricks.yml"));
  const run = options.run ?? runDatabricksCommand;
  const profileArgs = options.profile ? ["--profile", options.profile] : [];
  const env = options.env ?? process.env;
  const executable = options.executable ?? "databricks";

  const bundleArgs = [
    "bundle",
    "deploy",
    ...(options.target ? ["--target", options.target] : []),
    ...profileArgs,
  ] as const;
  options.onCommand?.({ executable, args: bundleArgs });
  const bundle = await run(executable, bundleArgs, { cwd: artifactDir, env });
  requireSuccess("Databricks bundle synchronization", bundle);

  if (options.startBeforeDeploy && options.appName) {
    const startArgs = ["apps", "start", options.appName, ...profileArgs] as const;
    options.onCommand?.({ executable, args: startArgs });
    const start = await run(executable, startArgs, { cwd: artifactDir, env });
    requireSuccess("Databricks App compute start", start);
  }

  const appArgs = [
    "apps",
    "deploy",
    ...(options.appName ? [options.appName] : []),
    ...(options.sourceCodePath ? ["--source-code-path", options.sourceCodePath] : []),
    ...(options.mode ? ["--mode", options.mode] : []),
    "--skip-validation",
    "--auto-approve",
    ...profileArgs,
  ] as const;
  options.onCommand?.({ executable, args: appArgs });
  const app = await run(executable, appArgs, { cwd: artifactDir, env });
  requireSuccess("Databricks App snapshot activation", app);

  return {
    bundleSynchronized: true,
    appSnapshotActivated: true,
    bundleOutput: lastNonEmptyLine(bundle.stdout),
    appOutput: lastNonEmptyLine(app.stdout),
  };
}

/** Destroy only resources declared by an already-built Databricks App bundle. */
export async function destroyDatabricksAppArtifact(
  options: DestroyDatabricksAppArtifactOptions,
): Promise<DestroyDatabricksAppArtifactResult> {
  const artifactDir = path.resolve(options.artifactDir);
  await access(path.join(artifactDir, "databricks.yml"));
  const run = options.run ?? runDatabricksCommand;
  const args = [
    "bundle",
    "destroy",
    "--auto-approve",
    ...(options.target ? ["--target", options.target] : []),
    ...(options.profile ? ["--profile", options.profile] : []),
  ] as const;
  const executable = options.executable ?? "databricks";
  options.onCommand?.({ executable, args });
  const result = await run(executable, args, {
    cwd: artifactDir,
    env: options.env ?? process.env,
  });
  requireSuccess("Databricks bundle teardown", result);
  return { destroyed: true, output: lastNonEmptyLine(result.stdout) };
}

export async function runDatabricksCommand(
  command: string,
  args: readonly string[],
  options: { cwd: string; env: NodeJS.ProcessEnv },
): Promise<DatabricksCommandResult> {
  return new Promise((resolve, reject) => {
    const child = spawn(command, [...args], {
      cwd: options.cwd,
      env: options.env,
      shell: false,
      stdio: ["ignore", "pipe", "pipe"],
    });
    let stdout = "";
    let stderr = "";
    child.stdout.on("data", (chunk: Buffer) => {
      stdout += chunk.toString();
    });
    child.stderr.on("data", (chunk: Buffer) => {
      stderr += chunk.toString();
    });
    child.once("error", reject);
    child.once("close", (code) => resolve({ exitCode: code ?? -1, stdout, stderr }));
  });
}

function requireSuccess(label: string, result: DatabricksCommandResult): void {
  if (result.exitCode === 0) return;
  const detail = result.stderr.trim().slice(-2_000);
  throw new Error(
    `${label} failed with exit code ${result.exitCode}${detail ? `: ${detail}` : ""}`,
  );
}

function lastNonEmptyLine(value: string): string {
  return (
    value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .at(-1) ?? ""
  );
}
